import { Box, Flex, Text, useBoolean } from '@chakra-ui/react';
import { ReactComponent as VenngageLogoIcon } from 'assets/icons/venngage_logo.svg';
import { TypingText } from 'modules/common/components/TypingText/TypingText';
import { memo, useEffect, useState } from 'react';

import { DEFAULT_CHAT_CONFIG, DELAY_TO_LIVE_READING } from '../Chat.config';
import { GeneratingMessageProps, MessageType } from '../Chat.types';

/**
 * Render the design generating message in the ChatPanel
 *
 * @param data.messages[0] Control the force stop of the typing animation: start | stop
 * @param data.messages[1] Text for the typing animation
 */
export const GeneratingMessage = memo(({ data, config }: GeneratingMessageProps) => {
  const text = data.messages[1];
  const shouldForceStop = data.messages[0] === 'stop';

  const [message, setMessage] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setMessage(text);
    }, DELAY_TO_LIVE_READING);

    return () => clearTimeout(timer);
  }, [text]);

  useEffect(() => {
    if (shouldForceStop) {
      setMessage('');
    }
  }, [shouldForceStop]);

  const loadingDotTypeSpeed =
    config[MessageType.BOT_GENERATING]?.loadingDotTypeSpeed ||
    DEFAULT_CHAT_CONFIG[MessageType.BOT_GENERATING].loadingDotTypeSpeed;

  const typeSpeed =
    config[MessageType.BOT_GENERATING]?.typeSpeed || DEFAULT_CHAT_CONFIG[MessageType.BOT_GENERATING].typeSpeed;

  const delayPerLine =
    config[MessageType.BOT_GENERATING]?.delayPerLine || DEFAULT_CHAT_CONFIG[MessageType.BOT_GENERATING]?.delayPerLine;
  const [isLoadingDots, setIsLoadingDots] = useBoolean(false);

  const ChatGeneratingMessageTestId = 'chat-generating-message';
  const ChatGeneratingMessageTextTestId = 'chat-generating-message-text';
  const ChatGeneratingMessageLoadingDotTextTestId = 'chat-generating-message-loadingdot-text';

  return (
    <Flex gap='4' data-testid={ChatGeneratingMessageTestId}>
      <Flex alignItems='flex-end'>
        <VenngageLogoIcon width='24px' height='24px' color='var(--vg-colors-brand-500)' />
      </Flex>
      <Box p={0} m={0} w={0} h={0} overflow={'hidden'} role={'log'} aria-live={'assertive'}>
        {message}
      </Box>
      <Box p='2' borderRadius='lg' bg='violet.50' flexGrow='1'>
        <TypingText
          text={text}
          typingSpeed={typeSpeed}
          delayPerLine={delayPerLine}
          forceStop={shouldForceStop}
          onComplete={setIsLoadingDots.on}
        >
          {(text) => (
            <Text whiteSpace='pre-line' data-testid={ChatGeneratingMessageTextTestId}>
              {text}
              {isLoadingDots && (
                <TypingText
                  text={'...'}
                  iterationCount={-1}
                  typingSpeed={loadingDotTypeSpeed}
                  delayPerLine={delayPerLine}
                  forceStop={shouldForceStop}
                >
                  {(text) => (
                    <Text as='span' data-testid={ChatGeneratingMessageLoadingDotTextTestId}>
                      {text}
                    </Text>
                  )}
                </TypingText>
              )}
            </Text>
          )}
        </TypingText>
      </Box>
    </Flex>
  );
});

GeneratingMessage.displayName = 'GeneratingMessage';
